import type { BrainAction, DesignComposerOperation } from "@afar-research/afar";
import { designComposerOperationCollectsEvidenceUrls } from "@afar-research/afar";
import type { ThreadActionSelection } from "@afar-research/contracts";
import { memo } from "react";
import {
  EllipsisIcon,
  FolderIcon,
  LinkIcon,
  PaperclipIcon,
  PlusIcon,
  ServerIcon,
  TypeIcon,
} from "lucide-react";

import { Button } from "../ui/button";
import {
  Menu,
  MenuGroup,
  MenuGroupLabel,
  MenuItem,
  MenuPopup,
  MenuSeparator,
  MenuSub,
  MenuSubPopup,
  MenuSubTrigger,
  MenuTrigger,
} from "../ui/menu";
import { Tooltip, TooltipPopup, TooltipTrigger } from "../ui/tooltip";
import { actionIconComponent } from "./actionIcons";
import type { ComposerActionEntry } from "./composerActions.logic";
import { composerFloatingLayerProps } from "./composerEventScope";

function fromThisComputerLabel(label: string, uploads: boolean): string {
  return uploads ? `Upload ${label.toLowerCase()}` : label;
}

function ActionMenuItem(props: {
  entry: ComposerActionEntry;
  onPick: (selection: ThreadActionSelection) => void;
}) {
  const Icon = actionIconComponent(props.entry.icon);
  const label = props.entry.run === "dialog" ? `${props.entry.label}…` : props.entry.label;
  return (
    <MenuItem
      disabled={props.entry.disabledReason !== null}
      onClick={() => props.onPick(props.entry.selection)}
      className="items-start py-1.5"
    >
      <Icon className="mt-0.5 size-4" />
      <span className="flex min-w-0 flex-col">
        <span>{label}</span>
        {/* An Action that cannot run here stays listed with the reason, never hidden. */}
        <span className="text-muted-foreground text-xs">
          {props.entry.disabledReason ?? props.entry.description}
        </span>
      </span>
    </MenuItem>
  );
}

/**
 * The composer's `+`: what to add to this message, then the Actions it can
 * carry (ADR 0142). Files and folders come first; the research verbs follow;
 * housekeeping and the Actions project or user skills declared sit under More.
 *
 * The trigger deliberately takes no `onPointerDown` handler. Base UI opens a
 * menu on `mousedown`, and preventing the default of `pointerdown` — as a
 * plain composer control does to keep the editor focused — suppresses the
 * compatibility mouse events, so the menu would never open at all.
 */
export const ComposerActionMenu = memo(function ComposerActionMenu(props: {
  entries: ReadonlyArray<ComposerActionEntry>;
  /** The Brain Action the chip holds, which decides what can be added. */
  brainAction: BrainAction | null;
  /** The Design Operation the chip holds; Add Example also takes a URL. */
  designOperation: DesignComposerOperation | null;
  /** False while the chip's Action takes no attachments (Lint, Delete). */
  takesAttachments: boolean;
  /**
   * Whether the primary pick takes folders too. Desktop and Mac Host Web open
   * one panel that takes files and folders together; the browser file input
   * every other surface falls back to takes files only.
   */
  filesPickTakesFolders: boolean;
  /**
   * The thread's Environment is on another machine, so a pick from this
   * computer uploads bytes rather than referencing a path there.
   */
  environmentIsElsewhere: boolean;
  disabled: boolean;
  onPickFilesAndFolders: () => void;
  /** Browse the files already on the Environment; null when the thread has no workspace. */
  onPickEnvironmentFiles: (() => void) | null;
  /**
   * Pick one folder on its own, beside a files-only primary pick; null where
   * this browser cannot pick a folder at all.
   */
  onPickFolder: (() => void) | null;
  onPickRawSources: () => void;
  onAddText: () => void;
  onAddEvidenceUrl: () => void;
  onPickAction: (selection: ThreadActionSelection) => void;
}) {
  const verbs = props.entries.filter((entry) => entry.group === "verb");
  const more = props.entries.filter((entry) => entry.group !== "verb");
  return (
    <Menu>
      <Tooltip>
        <MenuTrigger
          disabled={props.disabled}
          render={
            <TooltipTrigger
              render={
                <Button
                  type="button"
                  variant="ghost"
                  size="icon-sm"
                  // Base UI marks a disabled trigger with `data-disabled` and
                  // leaves the element enabled, so the control has to say so
                  // itself or it reads and looks live while doing nothing.
                  disabled={props.disabled}
                  aria-label="Add files, folders, or an action"
                />
              }
            />
          }
        >
          <PlusIcon />
        </MenuTrigger>
        <TooltipPopup>Add files or an action</TooltipPopup>
      </Tooltip>
      <MenuPopup align="start" className="max-w-80" {...composerFloatingLayerProps}>
        {props.takesAttachments || props.brainAction === "delete" ? (
          <MenuGroup>
            {/* Delete collects capture folders, not attachments: a file attached
                here would ride the turn and mean nothing. */}
            {props.brainAction === "delete" ? (
              <MenuItem onClick={props.onPickRawSources}>
                <FolderIcon className="size-4" />
                Raw sources
              </MenuItem>
            ) : (
              <MenuItem onClick={props.onPickFilesAndFolders}>
                <PaperclipIcon className="size-4" />
                {fromThisComputerLabel(
                  props.filesPickTakesFolders ? "Files or folders" : "Files",
                  props.environmentIsElsewhere,
                )}
              </MenuItem>
            )}
            {props.onPickFolder !== null && props.takesAttachments ? (
              <MenuItem onClick={props.onPickFolder}>
                <FolderIcon className="size-4" />
                {fromThisComputerLabel("Folder", props.environmentIsElsewhere)}
              </MenuItem>
            ) : null}
            {/* Files already on the Environment attach by their path there, which
                is the only way to reach them from a remote or cloud Environment. */}
            {props.onPickEnvironmentFiles !== null && props.takesAttachments ? (
              <MenuItem onClick={props.onPickEnvironmentFiles}>
                <ServerIcon className="size-4" />
                From the Environment
              </MenuItem>
            ) : null}
            {props.brainAction === "ingest" ? (
              <MenuItem onClick={props.onAddText}>
                <TypeIcon className="size-4" />
                Text
              </MenuItem>
            ) : null}
            {/* Evidence that has no file to attach still has a location, and
                Add Example is the one Design operation that preserves it. */}
            {props.designOperation !== null &&
            designComposerOperationCollectsEvidenceUrls(props.designOperation) ? (
              <MenuItem onClick={props.onAddEvidenceUrl}>
                <LinkIcon className="size-4" />
                URL
              </MenuItem>
            ) : null}
          </MenuGroup>
        ) : null}
        {props.takesAttachments || props.brainAction === "delete" ? <MenuSeparator /> : null}
        <MenuGroup>
          <MenuGroupLabel>Actions</MenuGroupLabel>
          {verbs.map((entry) => (
            <ActionMenuItem key={entry.selection.id} entry={entry} onPick={props.onPickAction} />
          ))}
        </MenuGroup>
        {more.length > 0 ? (
          <MenuSub>
            <MenuSubTrigger>
              <EllipsisIcon className="size-4" />
              More
            </MenuSubTrigger>
            <MenuSubPopup className="max-w-80" {...composerFloatingLayerProps}>
              {more.map((entry) => (
                <ActionMenuItem
                  key={entry.selection.id}
                  entry={entry}
                  onPick={props.onPickAction}
                />
              ))}
            </MenuSubPopup>
          </MenuSub>
        ) : null}
      </MenuPopup>
    </Menu>
  );
});
